/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ArrowsUpDownIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { deleteUser } from "~/services/supervisor.services";
import EditModal from "./modals/editUser";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
interface IUserTableProps {
  data: any[];
  onDataUpdate: (data: any[]) => void;
  fetchData: () => void;
}
type SelectedRowIds = Record<string, boolean>;
const UserTable = ({ data, onDataUpdate, fetchData }: IUserTableProps) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({
    key: "",
    direction: "asc",
  });
  const [filteredData, setFilteredData] = useState<any[]>(data);
  const [searchInputs, setSearchInputs] = useState<Record<string, string>>({
    employeeid: "",
    firstname: "",
    lastname: "",
    leadname: "",
    emailaddress: "",
    roles: "",
    groups: "",
    documenttypes: "",
    iswnsemployee: "",
    contactnumber: "",
    active: "",
    islocked: "",
    isloggedin: "",
    isresetrequired: "",
    invalidattemptcount: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [selectedRowIds, setSelectedRowIds] = useState<SelectedRowIds>({});
  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showSuccessModalReload, setShowSuccessModalReload] = useState(false);
  const [SuccessMessage, setSuccessMessage] = useState("");
  const columnHelper = createColumnHelper<any>();
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  // eslint-disable-next-line @typescript-eslint/ban-types
  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedSetSearch = useCallback(
    debounce((key: string, value: string) => {
      setSearchInputs((prev) => ({
        ...prev,
        [key]: value,
      }));
    }, 0), // Changed delay to 300 ms
    []
  );

  const handleInputChange = useCallback(
    (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      debouncedSetSearch(key, value);
    },
    [debouncedSetSearch]
  );

  const requestSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };
  const columnHeaders: Record<string, string> = {
    employeeid: "Employee Id",
    firstname: "First Name",
    lastname: "Last Name",
    leadname: "Lead Name",
    emailaddress: "Email Address",
    roles: "Roles",
    groups: "Groups",
    documenttypes: "Document Types",
    iswnsemployee: "Is WNS Employee",
    contactnumber: "Contact Number",
    active: "Active",
    islocked: "Is Locked",
    isloggedin: "Is Logged In",
    isresetrequired: "Is Reset Required",
    invalidattemptcount: "Invalid Attempt Count",
  };

  const columns = useMemo(() => {
    const createHeader = (key: string) => {
      // Get the display name from the mapping or use the key itself as fallback
      const headerName = columnHeaders[key] || key;

      return (
        <div className="p-2">
          <div className="flex flex-col gap-2 justify-center">
            <div className="flex gap-2 justify-center">{headerName}</div>
            <div className="flex items-center justify-center gap-2">
              <input
                type="text"
                value={searchInputs[key] || ""}
                onChange={handleInputChange(key)}
                // placeholder={`${headerName}`}
                className="border w-[140px] border-gray-300 rounded"
              />
              <button onClick={() => requestSort(key)}>
                <ArrowsUpDownIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      );
    };

    const headerEdit = <div className="p-2">Edit</div>;
    const headerDelete = <div className="p-2">Delete</div>;
    const headerSelect = (
      <input
        type="checkbox"
        checked={Object.keys(selectedRowIds).length === data.length} // Select all if all are selected
        onChange={() => {
          const allSelected =
            Object.keys(selectedRowIds).length === data.length;
          const newSelectedIds: SelectedRowIds = {};
          if (!allSelected) {
            data.forEach((row) => {
              newSelectedIds[row.id] = true; // Assuming row.id is unique
            });
          }
          setSelectedRowIds(newSelectedIds);
        }}
      />
    );

    return [
      {
        id: "select",
        header: headerSelect,
        cell: (props: any) => (
          <input
            type="checkbox"
            checked={selectedRowIds[props.row.original.id] || false}
            onChange={() => {
              setSelectedRowIds((prev) => {
                const newSelectedRowIds = { ...prev };
                if (newSelectedRowIds[props.row.original.id]) {
                  delete newSelectedRowIds[props.row.original.id];
                } else {
                  newSelectedRowIds[props.row.original.id] = true;
                }
                return newSelectedRowIds;
              });
            }}
          />
        ),
      },
      {
        id: "edit",
        header: headerEdit,
        cell: (props: any) => (
          <div
            className="flex items-center justify-center gap-2 cursor-pointer"
            onClick={() => handleEditClick(props.row.original.id)}
          >
            <PencilIcon className="h-4 w-4 text-blue-500" />
          </div>
        ),
      },
      {
        id: "delete",
        header: headerDelete,
        cell: (props: any) => (
          <div
            className="flex items-center justify-center gap-2 cursor-pointer"
            onClick={async () => {
              const confirmDelete = window.confirm(
                "Are you sure you want to delete this user?"
              );
              if (confirmDelete) {
                await handleDelete(props.row.original.id);
              }
            }}
          >
            <TrashIcon className="h-4 w-4 text-red-500" />
          </div>
        ),
      },
      ...[
        "employeeid",
        "firstname",
        "lastname",
        "leadname",
        "emailaddress",
        "roles",
        "groups",
        "documenttypes",
        "iswnsemployee",
        "contactnumber",
        "active",
        "islocked",
        "isloggedin",
        "isresetrequired",
        "invalidattemptcount",
      ].map((key) =>
        columnHelper.accessor(key, {
          header: createHeader(key) as any,
          cell: (props: any) => {
            const value = props.getValue();
            // Render boolean values as "Yes" or "No"
            if (typeof value === "boolean") {
              return <div>{value ? "Yes" : "No"}</div>;
            }
            return <div>{value}</div>;
          },
        })
      ),
    ];
  }, [columnHelper, searchInputs, sortConfig, handleInputChange]);

  useEffect(() => {
    // This is to avoid creation of timeout per key typed
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      const filtered = data.filter((row) =>
        Object.entries(searchInputs).every(
          ([key, searchTerm]) =>
            !searchTerm ||
            row[key]
              ?.toString()
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
        )
      );
      setFilteredData(filtered);
      setCurrentPage(0); // Reset to first page on search change
    }, 300);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [searchInputs, data]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a: any, b: any) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = currentPage * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedData.slice(start, end);
  }, [sortedData, currentPage, rowsPerPage]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(0); // Reset to first page
  };

  const tableObj = useReactTable({
    columns: columns as any,
    data: paginatedData,
    enableRowSelection: true,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleEditClick = async (userId: string) => {
    setSelectedUserId(userId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUserId(null);
  };

  const DeleteAll = async () => {
    const idsToDelete = Object.keys(selectedRowIds);
    const intKeys = idsToDelete.map((key) => parseInt(key, 10));

    if (idsToDelete.length === 0) {
      toast.error("No rows selected for deletion.", {
        theme: "colored",
      });
      return;
    }

    const confirmed= window.confirm("Are you sure you want to delete this user?");
    if (!confirmed) {
      return;
    }

    const deletePromises = idsToDelete.map((key) => deleteUser(key));
    const responses = await Promise.all(deletePromises);

    const allSuccessful = responses.every((response) => response.status);

    if (allSuccessful) {
      const updatedData = data.filter((user) => !intKeys.includes(user.id));
      ``;
      onDataUpdate(updatedData);

      // Clear row selection
      tableObj.setRowSelection({});

      setSuccessMessage("User(s) deleted successfully");
      setShowSuccessModalReload(true);
      setTimeout(() => {
        setShowSuccessModalReload(false);
      }, 3000);
    } else {
      toast.error("Something went wrong. Some deletions failed.", {
        theme: "colored",
      });
    }

    setSelectedRowIds({});
  };

  const handleDelete = async (userId: string) => {
    try {
      const response = await deleteUser(userId);
      // Check if the response indicates success
      if (response.status) {
        handleSuccess(userId);
      } else {
        handleFailure();
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleSuccess = (id: string) => {
    setSuccessMessage("User deleted successfully");
    setShowSuccessModalReload(true);

    setTimeout(() => {
      setShowSuccessModalReload(false);
    }, 3000);

    const updatedData = data.filter((user) => user.id !== id);
    onDataUpdate(updatedData); // Notify parent component of the data change
  };
  // Handle failure scenario
  const handleFailure = () => {
    setErrorMessage("Failed to delete user");
    console.error("Failed to delete user");
  };
  // Handle errors
  const handleError = (error: any) => {
    console.error("Error deleting user", error);
  };

  const handleExportToExcel = () => {
    // // Debugging
    // const selectedData = Array.from(selectedRows).map(
    //   (index: any) => sortedData[index]
    // );

    const keys = Object.keys(selectedRowIds);
    const intKeys = keys.map((key) => parseInt(key, 10));
    const selectedData = sortedData.filter((row) => intKeys.includes(row.id));
    if (selectedData.length === 0) {
      toast.error("No rows selected for export.", {
        theme: "colored",
      });
      return;
    }
    console.log(selectedData);
    // Map the selected rows to the desired structure
    const formattedRows = selectedData.map((row: any) => ({
      // UserID: row.ltluserid,
      "Employee ID": row.employeeid,
      "First Name": row.firstname,
      "Last Name": row.lastname,
      "Lead Name": row.leadname,
      "Email Address": row.emailaddress,
      Roles: row.roles,
      Documents: row.documenttypes,
      Groups: row.groups,
      "WNS Employee": row.iswnsemployee ? "Yes" : "No",
      "Contact Number": row.contactnumber,
      Active: row.active ? "Yes" : "No",
      Locked: row.islocked ? "Yes" : "No",
      Online: row.isloggedin ? "Yes" : "No",
      "Reset Password Required": row.isresetrequired ? "Yes" : "No",
      "Invalid Login Attempts": row.invalidattemptcount,
    }));
    const worksheet = XLSX.utils.json_to_sheet(formattedRows);
    // Calculate column widths based on headers and cell values
    const headers = Object.keys(formattedRows[0]);
    const columnWidths: number[] = headers.map((header) => {
      const headerLength = header.length;
      const maxCellLength = formattedRows.reduce(
        (max, row: Record<string, any>) => {
          const cellValue = row[header] ? row[header].toString() : "";
          return Math.max(max, cellValue.length);
        },
        0
      );
      return Math.max(headerLength, maxCellLength);
    });

    // Add a little extra space to each column width
    const widthSettings = columnWidths.map((width) => ({ wch: width + 5 }));

    worksheet["!cols"] = widthSettings;
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Output data");
    XLSX.writeFile(workbook, "Users.xlsx");
  };

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  return (
    <div className="w-full overflow-x-auto">
      <div className="flex justify-between mb-4">
        <div>
          <label>
            Rows per page:
            <select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              className="ml-2 border border-gray-300 rounded"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={sortedData.length}>All</option>
            </select>
          </label>
        </div>
        <div>
          <button
            onClick={handleExportToExcel}
            className="bg-orange-400 text-white py-1 px-2 rounded mr-1"
          >
            Export to Excel
          </button>
          <button
            onClick={DeleteAll}
            className="bg-orange-400 text-white py-1 px-2 rounded"
          >
            Delete
          </button>
        </div>
      </div>
      <div className="w-full overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {tableObj.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th className="p-2" key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableObj.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border border-neutral-300">
                {row.getVisibleCells().map((cell) => (
                  <td className="p-2" key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-4">
        <div className="p-4">
          Showing {paginatedData.length} of {filteredData.length} entries
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
            disabled={currentPage === 0}
            className="border border-gray-300 px-4 py-2 rounded mr-2"
          >
            Previous
          </button>
          <span>
            Page {currentPage + 1} of {totalPages}
          </span>
          <button
            onClick={() =>
              handlePageChange(Math.min(currentPage + 1, totalPages - 1))
            }
            disabled={currentPage >= totalPages - 1}
            className="border border-gray-300 px-4 py-2 rounded ml-2"
          >
            Next
          </button>
        </div>
      </div>
      {isModalOpen && selectedUserId && (
        <EditModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onEdit={fetchData}
          userId={selectedUserId}
        />
      )}
      {showSuccessModalReload && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-4 rounded shadow-lg">
            <table className="min-w-full mt-4 border-collapse border border-gray-200">
              <tbody>
                {SuccessMessage.split("/").map((part, index) => (
                  <tr key={index}>
                    <td className="border border-gray-300 p-2">
                      {part.trim()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
    </div>
  );
};
export default UserTable;
